import Honeybadger from '@honeybadger-io/js';
import { useCallback, useEffect, useState } from 'react';
/**
 * EventBus key prefix so that we keep the data of event bus localised in one key.
 *
 * @internal
 */
export const EVENT_BUS_STORE_KEY = 'EVENT_BUS_STORE_KEY';
/**
 * Initialise `cwMemoryStore` and `EVENT_BUS_STORE_KEY` if they haven't already been initialised
 *
 * @internal
 */
globalThis.cwMemoryStore = globalThis.cwMemoryStore || {};
globalThis.cwMemoryStore[EVENT_BUS_STORE_KEY] =
    globalThis.cwMemoryStore[EVENT_BUS_STORE_KEY] || {};
/**
 * Internal function used to save the data in `window.cwMemoryStore[EVENT_BUS_STORE_KEY]`
 *
 * @internal
 */
export const saveToMemory = (channel, data) => {
    const store = globalThis.cwMemoryStore[EVENT_BUS_STORE_KEY];
    if (!store[channel]) {
        store[channel] = [data];
    }
    else {
        store[channel].push(data);
    }
};
/**
 * Internal function used to retrieve data from `window.cwMemoryStore[EVENT_BUS_STORE_KEY]`
 *
 * @internal
 */
export const getFromMemory = (channel) => globalThis.cwMemoryStore[EVENT_BUS_STORE_KEY][channel];
/**
 * The subscribe function that we use to listen for events on a particular channel.
 *
 * **Important**
 * Not only do we get data on new events, when we use this function we immediately get the data that has already been
 * passed to this channel. The way that we get it is by the callback function being called the number of times past
 * events have been posted with the data for those events.
 *
 * ```ts
 * import { EventBus } from '@catawiki/runtime-toolkit';
 *
 * // We just console.log the data we receive
 * const someCallbackFnc: EventBus.EventBusCallback<{ count: string }> = (e) => {
 *   console.log(e.detail.count);
 * };
 *
 * // posting events for 'some-channel' before anyone is subscribed
 * EventBus.post('some-channel', { count: 'one' });
 * EventBus.post('some-channel', { count: 'two' });
 * EventBus.post('some-channel', { count: 'three' });
 *
 * // subscribe after some events have already been posted
 * EventBus.subscribe<{ count: string }>('some-channel', someCallbackFnc);
 *
 * setTimeout(() => {
 *    EventBus.post('some-channel', { count: 'four' });
 * }, 5000);
 *
 * // console output
 * // one
 * // two
 * // three
 * // ... 5 seconds
 * // four
 * ```
 */
export const subscribe = (channel, callback, options) => {
    const channelData = getFromMemory(channel);
    if (channelData && Array.isArray(channelData)) {
        channelData.forEach((data) => callback({ detail: data }));
    }
    window.addEventListener(`${EVENT_BUS_STORE_KEY}:${channel}`, callback, options);
};
/**
 * unsubscribe basically removes the event listener from memory. We should remember to use it especially in React
 * when we unmount a particular component that is using EventBus.
 *
 * ```ts
 * useEffect(() => {
 *    const someFunction = ...
 *
 *    subscribe<{ user: string }>('some-channel', someFunction);
 *    return () => {
 *      unsubscribe('some-channel', someFunction);
 *    }
 * }, [])
 * ```
 */
export const unsubscribe = (channel, callback) => {
    window.removeEventListener(`${EVENT_BUS_STORE_KEY}:${channel}`, callback);
};
/**
 * We use post to send a particular event with data to all the listeners to a particular channel. A thing to note is
 * that post also saves the events and their data in a global object so that new subscribers can handle that
 * data as well.
 *
 * ```ts
 * import { EventBus } from '@catawiki/runtime-toolkit';
 *
 * // We just console.log the data we receive
 * const someCallbackFnc: EventBus.EventBusCallback<{ count: string }> = (e) => {
 *   console.log(e.detail.count);
 * };
 *
 * // posting events for 'some-channel' before anyone is subscribed
 * EventBus.post('some-channel', { count: 'one' });
 * EventBus.post('some-channel', { count: 'two' });
 * EventBus.post('some-channel', { count: 'three' });
 *
 * // subscribe after some events have already been posted
 * EventBus.subscribe<{ count: string }>('some-channel', someCallbackFnc);
 *
 * setTimeout(() => {
 *    EventBus.post('some-channel', { count: 'four' });
 * }, 5000);
 *
 * // console output
 * // one
 * // two
 * // three
 * // ... 5 seconds
 * // four
 * ```
 *
 * **NOTE**
 * The function doesn't accept `null` or `undefined` as a valid value to be passed in the `data` parameter.
 * It will send a notification to Honeybadger and return false.
 */
export const post = (channel, data, options) => {
    if (data === null || typeof data === 'undefined') {
        Honeybadger.notify(new Error(`"data" is null/undefined on channel "${channel}". Event not sent.`), {
            fingerprint: channel,
            name: 'EventBus',
        });
        return false;
    }
    const event = new CustomEvent(`${EVENT_BUS_STORE_KEY}:${channel}`, {
        detail: data,
    });
    if (!options?.skipMemory) {
        saveToMemory(channel, data);
    }
    window.dispatchEvent(event);
    return true;
};
/**
 * A custom hook that lets you subscribe to a channel and get the value on
 * change. It also allows you to post to change this value.
 *
 * @returns {[T, (value: T) => void]} A tuple similar to the return value from
 * React's useState.  The first element is the current value, and the second
 * element is the setter (the post method).
 *
 * @example
 * import { EventBus } from '@catawiki/runtime-toolkit';
 * ...
 * const [value, postValue] = EventBus.useEventBus<number>('my-channel', 0);
 *
 * useEffect(() => {
 *   console.log('Value changed:', value);
 * }, [value]);
 *
 * const increment = () => {
 *   postValue(value + 1);
 * };
 */
export const useEventBus = (channel, initialState, subscribeOptions) => {
    const [state, setState] = useState(initialState);
    useEffect(() => {
        const callback = ({ detail }) => {
            setState(detail);
        };
        subscribe(channel, callback, subscribeOptions);
        return () => unsubscribe(channel, callback);
    }, [channel, subscribeOptions]);
    const postCb = useCallback((value) => {
        post(channel, value);
    }, [channel]);
    return [state, postCb];
};
export var Screen;
(function (Screen) {
    Screen["SIGN_IN"] = "SIGN_IN";
    Screen["SIGN_UP"] = "SIGN_UP";
    Screen["FORGOT_PASSWORD"] = "FORGOT_PASSWORD";
    Screen["REGISTRATION_FLOW"] = "REGISTRATION_FLOW";
    Screen["PHONE"] = "PHONE";
    Screen["PHONE_CONFIRM"] = "PHONE_CONFIRM";
    Screen["MARKETING_CONSENT"] = "MARKETING_CONSENT";
    Screen["ACCOUNT_NAME"] = "ACCOUNT_NAME";
    Screen["ADDRESS_FORM"] = "ADDRESS_FORM";
    Screen["EMAIL_CONFIRM"] = "EMAIL_CONFIRM";
    Screen["PRE_DROP_OFF"] = "PRE_DROP_OFF";
    Screen["RESET_PASSWORD"] = "RESET_PASSWORD";
    Screen["REQUEST_MFA"] = "REQUEST_MFA";
    Screen["MFA_OTP"] = "MFA_OTP";
})(Screen || (Screen = {}));
export var Journeys;
(function (Journeys) {
    Journeys["CHANGE_PHONE"] = "CHANGE_PHONE";
    Journeys["ON_BOARDING"] = "ON_BOARDING";
    Journeys["ON_BOARDING_M6"] = "ON_BOARDING_M6";
})(Journeys || (Journeys = {}));
export var Event;
(function (Event) {
    Event["UPDATE_USER_INFO"] = "UPDATE_USER_INFO";
    Event["SHOW_SCREEN"] = "SHOW_SCREEN";
    Event["CLOSE_SCREEN"] = "CLOSE_SCREEN";
    Event["SHOW_MFA_SCREEN"] = "SHOW_OTP_SCREEN";
    Event["MFA_RESULT"] = "MFA_RESULT";
})(Event || (Event = {}));
export var MFAResult;
(function (MFAResult) {
    MFAResult["SUCCESS"] = "SUCCESS";
    MFAResult["FAILURE"] = "FAILURE";
})(MFAResult || (MFAResult = {}));
export const showScreen = (detail) => {
    post(Event.SHOW_SCREEN, detail);
};
export const subscribeToScreenEvent = (listener) => {
    subscribe(Event.SHOW_SCREEN, listener);
    return () => unsubscribe(Event.SHOW_SCREEN, listener);
};
export const onCloseScreen = (detail) => {
    post(Event.CLOSE_SCREEN, detail);
};
export const subscribeToOnCloseScreenEvent = (listener) => {
    subscribe(Event.CLOSE_SCREEN, listener);
    return () => unsubscribe(Event.CLOSE_SCREEN, listener);
};
export const updateUserInfo = (detail) => {
    post(Event.UPDATE_USER_INFO, detail);
};
export const subscribeToUserInfoEvent = (listener) => {
    subscribe(Event.UPDATE_USER_INFO, listener);
    return () => unsubscribe(Event.UPDATE_USER_INFO, listener);
};
/**
 * Triggers and subscribe to the Multi-Factor Authentication (MFA) screen event with the provided details.
 *
 *
 * ```ts
 * const mfaDetails: MFAMeta = {
 *   display_text: 'Please select a factor',
 *   available_factors: [
 *     { name: 'SMS', display_text: 'Text Message', is_primary: true },
 *     { name: 'Email', display_text: 'Email', is_primary: false }
 *   ]
 * };
 * showMFAScreen(mfaDetails);
 * ```
 *
 */
export const showMFAScreen = (detail) => {
    post(Event.SHOW_MFA_SCREEN, detail);
};
export const subscribeToMFAScreenEvent = (listener, options) => {
    subscribe(Event.SHOW_MFA_SCREEN, listener, options);
    return () => unsubscribe(Event.SHOW_MFA_SCREEN, listener);
};
/**
 * Triggers and subscribe to  the Multi-Factor Authentication (MFA) result event with the provided status.
 *
 * @param status - The result status of the MFA process, either SUCCESS or FAILURE.
 *
 * ```ts
 * onMFAResult(MFAResult.SUCCESS);
 * ```
 *
 */
export const onMFAResult = (status) => {
    post(Event.MFA_RESULT, { status }, { skipMemory: true });
};
export const subscribeToOnMFAResult = (listener, options) => {
    subscribe(Event.MFA_RESULT, listener, options);
    return () => unsubscribe(Event.MFA_RESULT, listener);
};
